'use client';
import { useEffect, useMemo, useRef } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { PluginDockOperation, PluginDockViewProps, PluginOperationDockSource } from 'elowen-plugin-ui-kit';
import type { ComponentType } from 'react';
import { subscribePluginEvents } from './pluginEvents';
import { createDockRowReconciler } from './operationDockRows';

export interface LoadedPluginDockSource {
  plugin: string;
  locale: string;
  strings: Record<string, string>;
  desktopOnly: boolean;
  source: PluginOperationDockSource;
}
/** The dock item id of one plugin row. Exported so `open(id, plugin, { show })` can name the item it is
 *  about to reveal without restating the shape. */
export const pluginDockItemId = (plugin: string, operationId: string): string => `plugin:${plugin}:${operationId}`;
/** A row whose source contributes a view is a WINDOW, not a timed operation: the view's extra props are
 *  the source's own, and the dock needs to know the row is drawn by the plugin rather than by the shared
 *  progress dialog. */
export type PluginDockItem = PluginDockOperation & {
  kind: 'plugin-operation';
  plugin: string;
  operationId: string;
  view?: ComponentType<PluginDockViewProps>;
};
/** Whether a dock row came from a plugin source, which is what gives it its own glyph, caption and row id
 *  vocabulary. Core sources (environment, update, memory) have none of that. */
export const isPluginDockRow = (item: { kind: string } | undefined): item is PluginDockItem =>
  item?.kind === 'plugin-operation';
/** Whether a dock row is drawn by its source's own view (a window) rather than by the shared progress
 *  dialog. A window is opened and closed by the reader, so the dock must not time it out, and closing it
 *  must not acknowledge the row. */
export const isPluginDockWindow = (item: { kind: string; view?: unknown } | undefined): item is PluginDockItem =>
  isPluginDockRow(item) && item.view !== undefined;
const keyOf = (loaded: LoadedPluginDockSource, userId: number | undefined) =>
  ['plugin-dock-operations', loaded.plugin, loaded.source.eventKind, userId, loaded.locale] as const;

/** One cache per account, locale and source. Pushed rows received during a seed read win over it. */
export function usePluginDockOperations(sources: LoadedPluginDockSource[], userId: number | undefined, mobile: boolean | undefined) {
  const qc = useQueryClient();
  const reconciliation = useMemo(() => createDockRowReconciler<PluginDockOperation>(), []);
  const active = sources.filter(loaded => userId !== undefined && (!loaded.desktopOnly || mobile === false));
  const current = useRef(active);
  current.current = active;
  const read = (loaded: LoadedPluginDockSource, fetchRows: () => Promise<PluginDockOperation[]>) =>
    reconciliation.read(keyOf(loaded, userId), userId, fetchRows);
  const queries = useQueries({ queries: active.map(loaded => ({
    queryKey: keyOf(loaded, userId),
    staleTime: Infinity,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: false,
    queryFn: () => read(loaded, () => loaded.source.list(loaded.strings)),
  })) });
  const put = (loaded: LoadedPluginDockSource, row: PluginDockOperation) => {
    const key = keyOf(loaded, userId);
    qc.setQueryData<PluginDockOperation[]>(key, rows => reconciliation.put(key, userId, rows ?? [], row));
  };
  const putRef = useRef(put);
  putRef.current = put;
  useEffect(() => subscribePluginEvents(event => {
    const loaded = current.current.find(item => item.plugin === event.plugin && item.source.eventKind === event.kind);
    if (!loaded) return;
    try {
      const row = loaded.source.decodeEvent(event.data, loaded.strings);
      if (row.accountUserId === userId && row.initiator === 'ui') putRef.current(loaded, row);
    } catch (error) { console.error('Malformed plugin dock frame', error); }
  }), [userId]);
  return {
    items: active.flatMap((loaded, index) => (queries[index]?.data ?? []).map<PluginDockItem>(row => ({
      ...row, kind: 'plugin-operation', plugin: loaded.plugin, operationId: row.id,
      id: pluginDockItemId(loaded.plugin, row.id),
      ...(loaded.source.view ? { view: loaded.source.view } : {}),
    }))),
    track: async (loaded: LoadedPluginDockSource, id: string) => {
      const rows = await read(loaded, async () => [await loaded.source.get(id, loaded.strings)]);
      for (const row of rows) putRef.current(loaded, row);
    },
    acknowledge: async (item: PluginDockItem) => {
      const loaded = current.current.find(source => source.plugin === item.plugin);
      if (!loaded) throw new Error('Plugin dock source is unavailable');
      const receipt = await loaded.source.acknowledge(item.operationId);
      const key = keyOf(loaded, userId);
      // An older list read must not resurrect the result this account just acknowledged.
      const row: PluginDockOperation = { ...item, id: item.operationId, acknowledgedAt: receipt.acknowledgedAt };
      putRef.current(loaded, row);
      await qc.invalidateQueries({ queryKey: key });
    },
  };
}
